import React, { useEffect, useRef, Fragment, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useIntl } from "react-intl";
import moment from 'moment';

//Controls
import {
    TableView as Table,
    SearchInput,
    IconButton,
    useTablePagination,
    TableButtons,
    Pill,
    DynamicTooltip,
    MenuComponent,
    Tooltip,
    Icons
} from '@gpstrackit/gtc-react-controls';

//components
import { Grid } from '@mui/material';
import { TagsGroup } from 'Components/Tag';
import CaseInfo from '../events/modals/caseInfo'
import DriverInfo from '../events/modals/driverInfo';

import {
    setDriverFilters,
    sendGenericExport,
    savePreference,
    clearRedux,
    setDriverPerformanceMainTab
} from 'Redux/actions';
import { GetFormatForMoment, getTagColor, completeColumns } from 'Core/data/Helpers';
import './style.scss';

// Custom Components
import Preferences from "Core/containers/Preferences";

import { openModalAdvancedFilters } from './modals/advancedFilters/redux/slice';
import { reset, searchDriverPerformanceDrivers } from './redux/slice';
import { NotificationManager } from '../../../../components/Notifications';
import { openModalCreateDriverCoaching } from './modals/createDriverCoaching/redux/slice';

// Helpers
import { getInitialRange,removeDiacritics } from '../../.globals/helpers';

//Modals
import AdvancedFilters from './modals/advancedFilters';
import { useNavigate } from 'react-router-dom';
import CreateDriverCoaching from './modals/createDriverCoaching';

// Initial State
const INITIAL_STATE = {
    rangeDate: {
        start: moment.utc().subtract(7, 'days').startOf('day').toDate(),
        end: moment.utc().subtract(1, 'days').endOf('day').toDate()
    },
    timeFrame: 'last7days',
    tags: [],
    events: [],
    drivers: [],
    openSearch: "",
    riskScore: null,
}

// Helper function to parse URL parameters and convert them to filter format
// Helper function to parse URL parameters and convert them to filter format
// Uses prefixed parameters (driversRange, driversStartDate, etc.) to avoid conflicts with other tabs
const parseUrlToFilters = (urlParams, drivers, tags) => {
    const filters = { ...INITIAL_STATE };

    // Parse range parameter with 'drivers' prefix
    if (urlParams.get('driversRange')) {
        filters.timeFrame = urlParams.get('driversRange');
        let rangeDate = getInitialRange(filters.timeFrame)
        if (rangeDate) {
            filters.rangeDate = {
                start: rangeDate.start,
                end: rangeDate.end
            };
        }
    }

    // Parse custom date range with 'drivers' prefix
    if (urlParams.get('driversStartDate') && urlParams.get('driversEndDate')) {
        filters.timeFrame = 'custom';
        let rangeDate = getInitialRange('custom', urlParams.get('driversStartDate'), urlParams.get('driversEndDate'), true)
        filters.rangeDate = {
            start: rangeDate.start,
            end: rangeDate.end
        };
    }

    // Parse event names filter with 'drivers' prefix
    if (urlParams.get('driversEvents')) {
        filters.events = urlParams.get('driversEvents').split(',').filter(Boolean).map(event => ({
            id: event,
            name: event, // textField usado por Select component
            eventName: event,
            label: event,
            value: event
        }));
    }

    // Parse driver filter with 'drivers' prefix
    if (urlParams.get('driversDriverId')) {
        const driverIdsString = urlParams.get('driversDriverId');
        let currentDriver = drivers?.find(driver =>
            String(driver.driverId) === String(driverIdsString) ||
            String(driver.id) === String(driverIdsString)
        );
        if (currentDriver) {
            filters.drivers = [{ ...currentDriver }]
        }
    }

    // Parse tag filter with 'drivers' prefix
    if (urlParams.get('driversTagId')) {
        const tagIdsString = urlParams.get('driversTagId');

        let currentTag = tags?.find(tag =>
            String(tag.id) === String(tagIdsString)
        );
        if (currentTag) {
            filters.tags = [{ ...currentTag }]
        }
    }
    return filters;
};

const KEY_PREFERENCE = "DrpDriversTab";

const DriverPerformanceDriverView = () => {

    //intl
    const { messages, locale } = useIntl();

    const [selectedItems, setSelectedItems] = useState([]);
    const [formData, setFormData] = useState(INITIAL_STATE);
    const [preferences, setPreferences] = useState({});
    const [loadPreference, setLoadPreferences] = useState(false);
    const [loadColumns, setLoadColumns] = useState(false);
    const [page, setPage] = useState(1);
    const [urlParamsProcessed, setUrlParamsProcessed] = useState(false);

    const [data, setData] = useState([]);

    const dispatch = useDispatch();
    const navigate = useNavigate();

    // Refs
    const driverTableRef = useRef();
    const formDataRef = useRef(formData);

    const timeZoneUser = useSelector((state) => state?.securityRedux?.user?.timeZone);
    const driversSelect = useSelector(state => state?.dashboardDriverPerformanceSliceRedux?.filters?.drivers);
    const dateStart = useSelector(state => state?.dashboardDriverPerformanceSliceRedux?.filters?.dateStart);
    const dateEnd = useSelector(state => state?.dashboardDriverPerformanceSliceRedux?.filters?.dateEnd);
    const timeFrameOptionSelected = useSelector(state => state?.dashboardDriverPerformanceSliceRedux?.filters?.timeFrameOptionSelected)
    const driversData = useSelector(state => state?.dashboardDriverPerformanceSliceRedux?.drivers);
    const nextToken = useSelector(state => state?.dashboardDriverPerformanceSliceRedux?.nextToken);
    const error = useSelector(state => state?.dashboardDriverPerformanceSliceRedux?.error);
    const loading = useSelector(state => state?.dashboardDriverPerformanceSliceRedux?.loading);
    const exportResult = useSelector((state) => state?.commomRedux?.exportResult);
    const exportName = useSelector((state) => state?.commomRedux?.exportName);
    const isKph = useSelector((state) => state?.securityRedux?.user?.isKph);
    const dateformat = useSelector(state => state?.securityRedux?.user?.dateformat);
    const timeZone = useSelector(state => state?.securityRedux?.user?.timeZone);
    const drivers = useSelector((state) => state?.catalogsRedux?.drivers);
    const tags = useSelector((state) => state?.catalogsRedux?.tags);
    const authenticatedId = useSelector(state => state?.securityRedux?.authenticatedId);

    //modal advanced filters
    const openModalFilters = useSelector((state) => state?.driverPerformanceDriverTabAdvancedFiltersRedux?.open);

    const timeFrameOptions = [
        { id: "custom", label: messages?.['date_custom'] || 'Custom' },
        { id: "today", label: messages?.['date_today'] || 'Today' },
        { id: "yesterday", label: messages?.['date_yesterday'] || 'Yesterday' },
        { id: "week", label: messages?.['date_this_week'] || 'This Week' },
        { id: "lastWeek", label: messages?.['date_last_week'] || 'Last Week' },
        { id: "thisMonth", label: messages?.['date_this_month'] || 'This Month' },
        { id: "lastMonth", label: messages?.['date_last_month'] || 'Last Month' },
        { id: "quarter", label: messages?.['date_this_quarter'] || 'This Quarter' },
        { id: "lastQuarter", label: messages?.['date_last_quarter'] || 'Last Quarter' },
    ];

    const paginationEvents = useTablePagination({
        dispatch,
        action: searchDriverPerformanceDrivers,
        searchFields: ["openSearch"],
        tableRef: driverTableRef,
        sendGenericExport,
        exportName: "driversTab",
    });

    useEffect(() => {
        if (driversData) {
            setData(driversData);
        }
    }, [driversData]);

    // Keep the ref updated with the most recent formData value
    useEffect(() => {
        formDataRef.current = formData;
    }, [formData]);

    const applyChanges = (field, value) => {
        if (typeof field === 'object' && field !== null) {
            const newFilters = field;
            let updatedParams = {
                _conditions: buildConditions({ ...newFilters, page: 1 }),
            };
            // Reset the table page
            setPage(1);
            driverTableRef?.current?.setPage(1);

            dispatch(setDriverFilters({
                drivers: driversSelect,
                timeFrameOptionSelected: timeFrameOptions.find(option => option.id === newFilters?.timeFrame) || timeFrameOptions[0],
                dateStart: newFilters?.rangeDate?.start ? moment.utc(newFilters?.rangeDate?.start).startOf('day').format() : moment.utc().startOf('month').format(),
                dateEnd: newFilters?.rangeDate?.end ? moment.utc(newFilters?.rangeDate?.end).endOf('day').format() : moment.utc().endOf('month').format(),
                noDriver: false,
                tags: newFilters?.tags?.length > 0 ? newFilters?.tags.map(t => t.id) : [],
                events: newFilters?.events || []
            }))

            paginationEvents.refresh(updatedParams);
            return;
        }

        let updatedParams = {
            _conditions: buildConditions({ ...formData, page: 1 }),
        };

        dispatch(setDriverFilters({
            drivers: driversSelect,
            timeFrameOptionSelected: timeFrameOptions.find(option => option.id === formData?.timeFrame) || timeFrameOptions[0],
            dateStart: formData?.rangeDate?.start ? moment.utc(formData?.rangeDate?.start).startOf('day').format() : moment.utc().startOf('month').format(),
            dateEnd: formData?.rangeDate?.end ? moment.utc(formData?.rangeDate?.end).endOf('day').format() : moment.utc().endOf('month').format(),
            noDriver: /* noDriverFilter?.variant || */ false,
            tags: formData?.tags?.length > 0 ? formData?.tags.map(t => t.id) : [],
            events: formData?.events || []
        }))

        if (field === 'openSearch') {

            updatedParams = {
                _conditions: buildConditions({ ...formData, openSearch: value, page: 1 }),
            };
            setFormData(prev => ({ ...prev, openSearch: value }));
            if (window.typingTime) {
                clearTimeout(window.typingTime);
            }
            window.typingTime = setTimeout(() => {
                savePreferences([
                    {
                        key: "openSearch",
                        value: value
                    }
                ])
                paginationEvents.refresh(updatedParams);
            }, 1000);
        } else {
            paginationEvents.refresh(updatedParams);
        }
    }

    const hasFilters = useCallback(() => {
        const excludedKeys = ["rangeDate", "page", "openSearch"];
        const defaultValues = {
            timeFrame: "last7days",
            tags: [],
            events: [],
            drivers: [],
            openSearch: "",
            riskScore: null
        };

        return Object.keys(formData).some(key => {
            if (excludedKeys.includes(key)) {
                return false;
            }

            const currentValue = formData[key];
            const defaultValue = defaultValues[key];

            // For arrays, compare length and content
            if (Array.isArray(currentValue) && Array.isArray(defaultValue)) {
                return currentValue.length !== defaultValue.length ||
                    currentValue.some((item, index) => item !== defaultValue[index]);
            }

            // For other types, strict comparison
            return currentValue !== defaultValue;
        })
    }, [formData]);

    const getCustomerMenuOptions = () => [
        messages["driver_performance_driver_sendToCoaching"]
    ];

    const handleClickCustomerMenu = (type, driver) => {
        if (type === messages["driver_performance_driver_sendToCoaching"]) {
            dispatch(openModalCreateDriverCoaching({ open: true, drivers: [driver?.driverId] }));
        }
    };

    const handlerDriverLink = useCallback((driverId) => {
        const params = new URLSearchParams();
        // Use 'events' prefix since we're navigating to Events tab
        params.append('eventsDriverId', driverId);
        if (formDataRef.current?.timeFrame) {
            if (formDataRef.current?.timeFrame !== 'custom') {
                params.append('eventsRange', formDataRef.current.timeFrame);
            } else if (formDataRef.current.rangeDate) {
                params.append('eventsStartDate', formDataRef.current.rangeDate?.start);
                params.append('eventsEndDate', formDataRef.current.rangeDate?.end);
            }
        }
        const queryString = params.toString();
        dispatch(setDriverPerformanceMainTab(2));
        if (queryString) {
            navigate(`/app/driverPerformance/main?${queryString}`);
        } else {
            navigate('/app/driverPerformance/main');
        }
    }, []);

    const navigateToDriverDetail = useCallback((driverId) => {
        // IMPORTANT:
        // Build the range from the same conditions used to query the Drivers list.
        // This prevents timezone/end-of-day normalization from shifting the end date by +1 day.
        const conditions = buildConditions(formDataRef.current || {});
        const dateRangeCondition = conditions?.find((item) => item?.range)?.range;

        const startDateIso = dateRangeCondition?.GTE
            ? new Date(dateRangeCondition.GTE).toISOString()
            : (dateStart || moment.utc().startOf('month').toISOString());

        const endDateIso = dateRangeCondition?.LTE
            ? new Date(dateRangeCondition.LTE).toISOString()
            : (dateEnd || moment.utc().endOf('month').toISOString());

        const params = new URLSearchParams();
        params.append('startDate', startDateIso);
        params.append('endDate', endDateIso);
        params.append('sourceModule', 'driversTab');

        navigate(`/app/driverPerformance/driverDetail/${driverId}?${params.toString()}`);
    }, [navigate, dateStart, dateEnd]);

    const savePreferences = (preferencesArray) => {

        const urlSearchParams = new URLSearchParams(location.search);
        const urlParamsArray = Array.from(urlSearchParams.entries());
        const hasUrlParams = urlParamsArray.length > 0;

        // Check if the only URL param is 'tab=drivers'
        const onlyTabDriversParam = hasUrlParams && 
            urlParamsArray.length === 1 && 
            urlSearchParams.get('tab') === 'drivers';

        const isColumnChanging = preferencesArray.length ? preferencesArray?.some(pref => pref.key === 'columns') : false;

        if (!hasUrlParams || isColumnChanging || onlyTabDriversParam) {
            if (!Array.isArray(preferencesArray)) {
                console.error("Expected an array of preferences");
                return;
            }

            let updatedPreferences = { ...preferences } || {};

            preferencesArray.forEach(({ key, value }) => {
                updatedPreferences[key] = value;
            });

            setPreferences(updatedPreferences);

            dispatch(savePreference({
                entityName: "User",
                entityId: authenticatedId,
                preferenceKey: KEY_PREFERENCE,
                preferenceValue: updatedPreferences
            }));
        }
    };

    let currentColumns = [
        {
            key: 'driverId',
            title: messages?.['driver_performance_driver_id'],
            show: true,
            export: true,
            render: (row) => {
                return (
                    <div style={{ width: 80 }}>
                        <a
                            href='#'
                            noWrap
                            style={{
                                color: '#005bf8',
                                textDecoration: 'underline',
                                cursor: 'pointer'
                            }}
                            onClick={() => {
                                navigateToDriverDetail(row.driverId);
                            }} >
                            {`${Math.round(row?.driverId || 0)}`}
                        </a>
                    </div>
                )
            },
            exportValue: (row) => {
                return Math.round(row?.driverId || 0);
            },
            functionString: 'return row?.driverId || ""'
        },
        {
            key: 'driverName',
            title: messages?.['driver_performance_driver_driver'], //name
            show: true,
            export: true,
            width: 210,
            render: (row) => {
                const driverKey = `drivertab-${row?.driverId}`;
                return (
                    <Grid container alignItems="center">
                        <Grid item xs={10}>
                            {
                                row?.driverName ? (
                                    <DynamicTooltip
                                        key={row?.id}
                                        trigger={<span style={{ fontFamily: 'Outfit, sans-serif', cursor: 'pointer', fontSize: 14, fontWeight: 400, borderBottom: '1px dashed #595151cc', display: 'inline-block' }}>{row.driverName}</span>}
                                        openOn="click"
                                        size="medium"
                                        position="top"
                                    >
                                        {(isOpen) => (
                                            <DriverInfo
                                                driverKey={driverKey}
                                                openTooltip={isOpen}
                                                driverId={row?.driverId}
                                            />
                                        )}
                                    </DynamicTooltip>
                                ) : <>-</>
                            }
                        </Grid>
                        <Grid item xs={2}>
                            {
                                row?.driverName ? (
                                    <MenuComponent
                                        placement="right-start"
                                        options={getCustomerMenuOptions()}
                                        icon="more_vert"
                                        onClick={handleClickCustomerMenu}
                                        value={row}
                                    />
                                ) : <></>
                            }
                        </Grid>
                    </Grid>
                )
            },
            exportValue: (row) => {
                return row?.driverName;
            },
            functionString: 'return row?.driverName || ""'
        },
        {
            key: 'avgEvents',
            title: messages?.['driver_performance_driver_avgEvents'], //name
            show: true,
            export: true,
            render: (row) => {
                return (
                    <div style={{ width: 100 }}>
                        <div style={{ width: 100 }}>
                            <a noWrap>
                                {`${Math.round(row?.avgEvents || 0)}`}
                            </a>
                        </div>
                    </div>
                )
            },
            exportValue: (row) => {
                return row?.avgEvents ? Math.round(row?.avgEvents || 0) : "0";
            },
            functionString: 'return Math.round(row?.avgEvents || 0) || "0"'
        },
        {
            key: 'riskScore',
            title: messages?.['driver_performance_driver_RiskScore'], //name
            show: true,
            export: true,
            render: (row) => {
                return (
                    <div style={{ width: 100 }}>
                        <a noWrap>
                            {`${row?.riskScore}`}
                        </a>
                    </div>
                )
            },
            exportValue: (row) => {
                return row?.riskScore || "0";
            },
            functionString: 'return row?.riskScore || "0"'
        },
        {
            key: 'riskEvents',
            title: messages?.['driver_performance_driver_riskEvents'], //name
            show: true,
            export: true,
            render: (row) => {
                const riskEventsCount = row?.riskEvents ?? 0;
                const hasRiskEvents = riskEventsCount > 0;

                return (
                    <div style={{ width: 100 }}>
                        {hasRiskEvents ? (
                            <a
                                href='#'
                                noWrap
                                style={{
                                    color: '#005bf8',
                                    textDecoration: 'underline',
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    handlerDriverLink(row.driverId)
                                }}
                            >
                                {riskEventsCount}
                            </a>
                        ) : (
                            <span>{riskEventsCount}</span>
                        )}
                    </div>
                )
            },
            exportValue: (row) => {
                return row?.riskEvents ?? 0;
            },
            functionString: 'return row?.riskEvents ?? 0'
        },
        {
            key: 'totalDistance',
            title: messages?.['driver_performance_driver_totalDistance'], //name
            show: true,
            export: true,
            render: (row) => {
                return (
                    <div style={{ width: 120 }}>
                        <a noWrap>
                            {`${Math.round(!isKph ? row?.totalDistance || 0 : (row?.totalDistance || 0) * 1.60934)} ${isKph ? messages?.['kilometers'] || 'km' : messages?.['miles'] || 'mi'}`}
                        </a>
                    </div>
                )
            },
            exportValue: (row) => {
                const distance = Math.round(!isKph ? row?.totalDistance || 0 : (row?.totalDistance || 0) * 1.60934);
                const unit = isKph ? messages?.['kilometers'] || 'km' : messages?.['miles'] || 'mi';
                return `${distance} ${unit}`;
            },
            functionString: `
                const translations = {
                    'kilometers': '${messages?.['kilometers'] || 'km'}',
                    'miles': '${messages?.['miles'] || 'mi'}'
                };
                
                const isKph = ${isKph};
                const distance = Math.round(isKph ? (row?.totalDistance || 0) * 1.60934 : row?.totalDistance || 0);
                const unit = isKph ? translations.kilometers : translations.miles;
                
                return distance + ' ' + unit;
            `
        },
        {
            key: 'lastCase',
            title: messages?.['driver_performance_driver_lastCoaching'], //name
            show: true,
            export: true,
            render: (row) => {
                return (
                    <div style={{ width: 160 }}>
                        {row?.lastCase ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'nowrap' }}>
                                <DynamicTooltip
                                    key={row?.id}
                                    trigger={<span style={{ fontFamily: 'Outfit, sans-serif', cursor: 'pointer', fontSize: 14, fontWeight: 400, borderBottom: '1px dashed #595151cc', display: 'inline-block' }}>{row?.lastCase}</span>}
                                    openOn="click"
                                    size="medium"
                                    position="top"
                                >
                                    {(isOpen) => (
                                        <CaseInfo
                                            caseNumber={row?.lastCase}
                                            openTooltip={isOpen}
                                        />
                                    )}
                                </DynamicTooltip>
                                {row?.lastCaseStatusId && <a style={{ whiteSpace: 'nowrap' }}>{messages[`driver_performance_case_${row?.lastCaseStatusId}`] || row?.lastCaseStatusId}</a>}
                            </div>
                        ) : (
                            <span>-</span>
                        )}
                    </div>
                )
            },
            exportValue: (row) => {
                const statusTranslated = row?.lastCaseStatusId ? (messages[`driver_performance_case_${row?.lastCaseStatusId}`] || row?.lastCaseStatusId) : '';
                return row?.lastCase ? `${row.lastCase} ${statusTranslated}` : '';
            },
            functionString: `
                const translations = {
                    ${Object.keys(messages || {})
                        .filter(key => key.startsWith('driver_performance_case_'))
                        .map(key => `'${key}': '${messages[key]}'`)
                        .join(',\n                    ')}
                };
                
                const statusId = row?.lastCaseStatusId;
                const statusTranslated = statusId ? (translations['driver_performance_case_' + statusId] || statusId) : '';
                return row?.lastCase ? row.lastCase + ' ' + statusTranslated : '';
            `
        },
        {
            key: 'driverTags',
            show: true,
            title: messages?.['driver_performance_driver_Tags'],
            width: 200,
            export: true,
            render: (item) => {
                let driverTags = [];
                try {
                    if (item?.driverTags?.includes("-99") || item?.driverTags?.includes(-99)) {
                        driverTags = [{ id: -99, label: messages?.['tagPicker_allTagsAreSelected'] || 'All Tags', color: '#005bf8' }];
                    } else if (item?.driverTags?.length > 0) {
                        driverTags = (item.driverTags || [])
                            .map(tagId => {
                                const tagElement = tags?.find(tag => String(tag.id) === String(tagId));
                                return tagElement ? { ...tagElement, color: getTagColor(tagElement.color) } : null;
                            })
                            .filter(tag => tag !== null && tag?.id !== -1);
                    }
                } catch (error) {

                }
                return (
                    <div style={{ width: 200 }}>
                        <TagsGroup
                            options={driverTags}
                            nameField="label"
                            colorField="color"
                        />
                    </div>
                );
            },
            exportValue: (row) => {
                if (!row?.driverTagsNames) return "";
                return row?.driverTagsNames?.join("/\r\n") || "";
            },
            functionString: 'return row?.driverTagsNames?.join(", ") || "";'
        }
    ];

    useEffect(() => {
        paginationEvents.setColumns(currentColumns);
        dispatch(setDriverFilters({
            drivers: driversSelect,
            timeFrameOptionSelected: timeFrameOptions[5], //select by default the current month
            dateStart: moment.utc().startOf('month').format(),
            dateEnd: moment.utc(new Date()).format(),
            noDriver: false
        }))

        setSelectedItems([]);
        driverTableRef?.current?.setPage(1);
        driverTableRef?.current?.setSelectedItems([]);

        return () => {
            dispatch(reset());
        };

    }, []);

    useEffect(() => {
        if (error) {
            NotificationManager.error(messages?.["errorMessage"] || "An error occurred", messages?.["error"] || "Error", 3000);
            dispatch(reset({ error: null }));
        }
        if (exportResult && exportName === "eventsTab") {
            NotificationManager.info(messages?.["exportInProcessBody"] || "Export in process", messages?.["exportInProcessTitle"] || "Export", 4000);
            dispatch(clearRedux("COMMON", { exportResult: null, exportName: null }));
        }
    }, [error, exportResult, exportName, messages, dispatch]);

    useEffect(() => {
        if (preferences?.columns?.length && !loadColumns) {
            const preferenceMap = new Map(preferences.columns.map(col => [col.key, col]));
            const reorderedColumns = preferences.columns
                .map(pref => {
                    const col = currentColumns.find(c => c.key === pref.key);
                    return col ? { ...col, show: pref.show } : null;
                })
                .filter(Boolean);
            const newColumns = currentColumns
                .filter(col => !preferenceMap.has(col.key))
                .map(col => ({ ...col, show: true }));

            const finalColumns = [...reorderedColumns, ...newColumns];

            paginationEvents.setColumns(finalColumns);
            setPreferences({ ...preferences });
            setLoadColumns(true);
        }
    }, [preferences?.columns, loadColumns])

    useEffect(() => {
        if (paginationEvents.columns.length > 0) {
            setPreferences({ ...preferences, columns: paginationEvents.columns })
            setLoadColumns(false);
        }
    }, [locale, timeZone, dateformat, timeZoneUser, isKph, tags]);

    const generateExportStyles = () => {
        return {
            0: {
                cellWidth: 60,
                fontSize: 11,
                fontStyle: "bold",
            }
        };
    };

    return (
        <Fragment>
            <Grid
                container
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                style={{ marginBottom: 8 }}
            >
                <Grid item xs={6} sm={2} md={2}>
                    <Grid
                        container
                        direction="row"
                        justifyContent="flex-start"
                        alignItems="center"
                        spacing={1}
                    >
                        <Grid item style={{ display: "flex" }}>
                            <IconButton
                                tooltip={messages?.["filters"] || "Filters"}
                                placement="bottom"
                                icon="filter_list"
                                size="small"
                                disabled={true || loading}
                                onClick={() => {
                                    dispatch(openModalAdvancedFilters({ open: !openModalFilters }));
                                }}
                                className={hasFilters() ? "driverPerformance-eventsTab-filters-available" : ""}
                            />
                            <TableButtons
                                {...paginationEvents}
                                messages={messages}
                                buttons={["export", "orderColumn"]}
                                onExport={(type) => {
                                    // Local export (CSV/PDF) when items are selected
                                    if (selectedItems?.length) {
                                        if (type === "Csv") {
                                            paginationEvents?.tableRef.current.exportCSV();
                                        } else if (type === "Pdf") {
                                            paginationEvents?.tableRef.current.exportPDF();
                                        }
                                    } else {
                                        // Remote export for full dataset
                                        let headers = paginationEvents.columns.filter(col => col.export && col.show)
                                            .map(col => ({
                                                header: removeDiacritics(col.title),
                                                column: col.key,
                                                functionString: col.functionString || `return row?.${col.key}`
                                            }));

                                        const conditions = buildConditions({ ...formData });

                                        let dateRange = conditions?.find((item) => item.range);
                                        
                                        let tags = conditions?.find((item) => item.tagIds);
                                        let events = conditions?.find((item) => item.events);
                                        let drivers = conditions?.find((item) => item.drivers);
                                        let openSearch = conditions?.find((item) => item.openSearch);

                                        let payload = {
                                            startDate: dateRange?.range?.GTE || moment().subtract(1, 'month').startOf('day').toISOString(),
                                            endDate: dateRange?.range?.LTE || moment().endOf('day').toISOString(),
                                            driverIds: drivers?.drivers || [],
                                            eventNames: events?.events?.length > 0 ? [...events.events] : [],
                                            driverName: openSearch?.openSearch || "",
                                            tagIds: tags?.tagIds?.length > 0 && !tags?.tagIds?.includes(-99) ? tags?.tagIds : [],
                                            groupBy: "driver"
                                        }

                                        dispatch(
                                            sendGenericExport(
                                                "eventsTab",
                                                type,
                                                searchDriverPerformanceDrivers.query,
                                                payload,
                                                headers,
                                                {
                                                    unit: 'px',
                                                    size: 'A4',
                                                    orientation: 'landscape',
                                                    fontSize: 10,
                                                },
                                                null,
                                                true,
                                                true,
                                                generateExportStyles()
                                            )
                                        );
                                    }
                                }}
                                onChangeColumns={(columns) => {
                                    savePreferences([{ key: "columns", value: columns }])
                                    paginationEvents.setColumns(columns)
                                }}
                                columns={paginationEvents.columns}
                            />
                            {
                                selectedItems?.length > 0 && (
                                    <IconButton
                                        tooltip={messages?.['driver_performance_driver_sendToCoaching']}
                                        placement="bottom"
                                        icon="share_location"
                                        size="small"
                                        disabled={true || loading}
                                        onClick={() => {
                                            let driverIds = selectedItems.map(item => item?.driverId).filter(Boolean);
                                            dispatch(openModalCreateDriverCoaching({ open: true, drivers: driverIds }));
                                        }}
                                        style={{ color: '#333' }}
                                    />
                                )
                            }
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={6} sm={4} md={4} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'nowrap' }}>
                    <Tooltip
                        placement="bottom"
                        title={messages['driver_performance_driver_timeInfo_message']}
                    >
                        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                            <Icons name="help" size="medium" />
                        </span>
                    </Tooltip>
                    <Pill
                        textField={"label"}
                        item={{ id: "date", label: `${messages[`driver_performance_events_${formData?.timeFrame}`] || formData?.timeFrame} (${formData?.rangeDate?.start ? moment(formData.rangeDate.start).format(`${GetFormatForMoment(dateformat) || 'MM/DD/YYYY'} hh:mm a`) : ''} - ${formData?.rangeDate?.end ? moment(formData.rangeDate.end).format(`${GetFormatForMoment(dateformat) || 'MM/DD/YYYY'} hh:mm a`) : ''})`, color: '#005bf8' }}
                        variant='filled'
                        onClick={() => {
                            dispatch(openModalAdvancedFilters({ open: !openModalFilters }));
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={4} className="mb-10 text-right">
                    <SearchInput
                        placeholder={messages?.["driver_performance_driver_searchDriver"]}
                        searchText={formData?.openSearch || ""}
                        onSearchText={(value) => applyChanges('openSearch', value)}
                    />
                </Grid>
            </Grid>


            <Preferences
                preferencesKey={KEY_PREFERENCE}
                onInitialPreferences={(data) => {
                    if (!loadPreference) {
                        try {
                            // Check for URL parameters first
                            const urlSearchParams = new URLSearchParams(location.search);
                            
                            // Only consider relevant URL parameters for Drivers tab (with 'drivers' prefix)
                            const relevantDriversParams = ['driversRange', 'driversStartDate', 'driversEndDate', 'driversEvents', 'driversDriverId', 'driversTagId'];
                            const hasRelevantUrlParams = relevantDriversParams.some(param => urlSearchParams.has(param));

                            let updatedFormData;
                            
                            if (hasRelevantUrlParams && !urlParamsProcessed) {
                                // Priority: URL parameters over user preferences
                                const filtersFromUrl = parseUrlToFilters(urlSearchParams, drivers, tags);
                                updatedFormData = { ...filtersFromUrl, page: 1 };
                                setUrlParamsProcessed(true);
                            } else {
                                // Fallback: Use user preferences
                                updatedFormData = {
                                    ...formData, ...['rangeDate', 'tags', 'events', 'drivers', 'riskScore'].reduce((acc, key) => {
                                        if (data?.[key]) acc[key] = data[key];
                                        return acc;
                                    }, {}),
                                    page: 1
                                };
                                if (data?.timeFrame) {
                                    const timeFrame = getInitialRange(data.timeFrame, data?.rangeDate?.start, data?.rangeDate?.end);
                                    if (timeFrame?.start && timeFrame?.end) {
                                        updatedFormData = {
                                            ...updatedFormData,
                                            rangeDate: timeFrame,
                                            timeFrame: data.timeFrame
                                        }
                                    }
                                }
                            }
                            const currentConditions = buildConditions(updatedFormData);
                            paginationEvents.refresh({ _conditions: currentConditions });
                            setFormData(updatedFormData)
                            setPreferences(data)
                            setLoadPreferences(true)

                            dispatch(setDriverFilters({
                                drivers: driversSelect,
                                timeFrameOptionSelected: timeFrameOptions.find(option => option.id === updatedFormData?.timeFrame) || timeFrameOptions[0],
                                dateStart: updatedFormData?.rangeDate?.start ? moment.utc(updatedFormData?.rangeDate?.start).startOf('day').format() : moment.utc().startOf('month').format(),
                                dateEnd: updatedFormData?.rangeDate?.end ? moment.utc(updatedFormData?.rangeDate?.end).endOf('day').format() : moment.utc().endOf('month').format(),
                                noDriver: false,
                                tags: updatedFormData?.tags?.length > 0 ? updatedFormData?.tags.map(t => t.id) : [],
                                events: updatedFormData?.events || []
                            }))
                        } catch (error) {

                        }
                    }
                }}
            >
                <Table
                    {...paginationEvents}
                    keyColumn="driverId"
                    data={data || []}
                    loading={loading}
                    topheight={250}
                    exportOrientation="landscape"
                    onSelectedItems={(items) => setSelectedItems(items)}
                    onChangeColumns={(columns) => {
                        let currentColumns = paginationEvents?.columns || []
                        let finalsColumns = completeColumns(currentColumns, columns)
                        savePreferences([{ key: "columns", value: finalsColumns }])
                        paginationEvents.setColumns(finalsColumns)
                    }}

                    infiniteScroll={true}
                    onChangePage={() => {
                        if (data?.length && nextToken) {
                            let newPage = page + 1
                            setPage(newPage);
                            const currentConditions = buildConditions({ ...formData, page: newPage, nextToken });
                            paginationEvents.refresh({ _conditions: currentConditions });
                        }
                    }}
                    nextToken={nextToken}
                    noMoreItemsMessage={messages?.['driver_performance_noMoreItems']}
                />
            </Preferences>
            <AdvancedFilters
                tableRef={paginationEvents}
                formData={formData}
                setFormData={setFormData}
                initialState={INITIAL_STATE}
                applyChanges={applyChanges}
                hasFilters={hasFilters}
                savePreferences={savePreferences}
            />
            <CreateDriverCoaching />
        </Fragment>
    );
};

const buildConditions = (formData) => {
    const conditions = [];

    // Validate that rangeDate exists before using it
    if (formData?.rangeDate?.start && formData?.rangeDate?.end) {
        const startUTC = formData?.timeFrame === 'custom'
            ? moment.utc(moment(formData.rangeDate.start).format('YYYY-MM-DD')).startOf('day').toDate()
            : formData.rangeDate.start;
        const endUTC = formData?.timeFrame === 'custom'
            ? moment.utc(moment(formData.rangeDate.end).format('YYYY-MM-DD')).endOf('day').toDate()
            : formData.rangeDate.end;

        conditions.push({ range: { fields: ["scheduledDate"], GTE: startUTC, LTE: endUTC } });
    }

    let allEvents = [];
    if (formData?.events?.length) {
        allEvents = [...allEvents, ...formData.events.map(event => event?.id).filter(Boolean)];
        if (allEvents.length > 0) {
            conditions.push({ events: allEvents });
        }
    }

    let allDrivers = [];
    if (formData?.drivers?.length) {
        allDrivers = [...allDrivers, ...formData.drivers.map(driver => driver?.driverId || driver?.id).filter(Boolean)];
        if (allDrivers.length > 0) {
            conditions.push({ drivers: allDrivers });
        }
    }

    let allTags = [];
    if (formData?.tags?.length) {
        allTags = [...allTags, ...formData.tags.map(tag => tag?.id).filter(Boolean)];
        if (allTags.length > 0) {
            conditions.push({ tagIds: allTags });
        }
    }

    if (formData.page) {
        conditions.push({ page: formData.page });
    }

    if (formData.openSearch) {
        conditions.push({ openSearch: formData.openSearch });
    }

    if (formData.nextToken) {
        conditions.push({ nextToken: formData.nextToken });
    }

    if( formData.riskScore !== null && formData.riskScore !== undefined && formData.riskScore !== '' ) {
        conditions.push({ riskScore: formData.riskScore });
    }

    return conditions;
};

export default DriverPerformanceDriverView;
